import React from 'react'
import { cn } from 'src/enviroment/lib/utils'
import { Slot } from '@radix-ui/react-slot'
import { IconLoader2 } from '@tabler/icons-react'
import Typography from '../Typography'

export interface SecondaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  rounded?: boolean
}

const sizeClasses = {
  sm: 'h-10 px-4 text-sm', // Small
  md: 'h-12 px-6 text-base', // Medium (default)
  lg: 'h-14 px-8 text-base', // Large
}

const SecondaryButton = React.forwardRef<HTMLButtonElement, SecondaryButtonProps>(
  (
    {
      className,
      asChild = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      size = 'md',
      fullWidth = false,
      rounded = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    const isDisabled = disabled || isLoading

    return (
      <Comp
        className={cn(
          // Base styles
          'relative inline-flex cursor-pointer items-center justify-center',
          'border transition-colors duration-200 active:scale-95',

          // Border radius - default or rounded (100px)
          'rounded-full',

          // Default state
          'border-light-green-500 bg-transparent text-gray-50',

          // States
          'hover:border-light-green-400 hover:bg-light-green-500/10 hover:text-gray-50', // Hover
          'active:border-light-green-600 active:bg-light-green-500/20 active:text-gray-50', // Pressed
          'disabled:cursor-not-allowed disabled:border-light-green-900 disabled:bg-transparent disabled:text-light-green-900', // Disabled

          // Focus
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-light-green-500 focus-visible:ring-offset-2',

          // Sizes
          sizeClasses[size],

          // Full width option
          fullWidth && 'w-full',

          className,
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {isLoading ? (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <IconLoader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="flex cursor-pointer items-center gap-2">
            {leftIcon}
            <Typography variant="base-semibold">{children}</Typography>
            {rightIcon}
          </div>
        )}
      </Comp>
    )
  },
)

SecondaryButton.displayName = 'SecondaryButton'

export { SecondaryButton }
